import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  createSupplier,
  listPurchases,
  listSuppliers,
  setSupplierActive,
} from "../../services/purchaseService";

const emptyForm = { companyName: "", phone: "", address: "" };
const focusableSelector =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error || fallback;

const formatCurrency = (value) =>
  Math.round(Number(value) || 0).toLocaleString("en-US");

function SupplierDialog({ onCancel, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef(null);
  const companyRef = useRef(null);

  useEffect(() => companyRef.current?.focus(), []);

  const handleKeyDown = (event) => {
    if (event.key === "Escape" && !saving) {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key !== "Tab") return;
    const controls = Array.from(
      dialogRef.current?.querySelectorAll(focusableSelector) || [],
    );
    if (!controls.length) return;
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const updateField = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
    if (event.target.name === "companyName") setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.companyName.trim()) {
      setError("Company name is required. / ကုမ္ပဏီအမည် ထည့်ပါ။");
      companyRef.current?.focus();
      return;
    }
    setSaving(true);
    setError("");
    try {
      const supplier = await createSupplier(form);
      onCreated(supplier);
    } catch (saveError) {
      setError(getErrorMessage(saveError, "Supplier could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay supplier-modal-overlay">
      <div
        ref={dialogRef}
        className="modal supplier-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="supplier-dialog-title"
        onKeyDown={handleKeyDown}
      >
        <div className="modal-header">
          <div>
            <h2 id="supplier-dialog-title">New supplier</h2>
            <p>ပစ္စည်းပေးသွင်းသူ အသစ်ထည့်ပါ။</p>
          </div>
          <button
            type="button"
            className="close-btn"
            aria-label="Close supplier form"
            onClick={onCancel}
            disabled={saving}
          >
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="supplier-company-name">Company name *</label>
            <input
              ref={companyRef}
              id="supplier-company-name"
              name="companyName"
              value={form.companyName}
              onChange={updateField}
              required
              aria-describedby="supplier-company-helper supplier-form-error"
              aria-invalid={Boolean(error)}
            />
            <small id="supplier-company-helper">ကုမ္ပဏီအမည်</small>
          </div>
          <div className="supplier-form-row">
            <div className="form-group">
              <label htmlFor="supplier-phone">Phone (optional)</label>
              <input
                id="supplier-phone"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={updateField}
              />
              <small>ဖုန်းနံပါတ် (မထည့်လည်းရသည်)</small>
            </div>
            <div className="form-group">
              <label htmlFor="supplier-address">Address (optional)</label>
              <input
                id="supplier-address"
                name="address"
                value={form.address}
                onChange={updateField}
              />
              <small>လိပ်စာ (မထည့်လည်းရသည်)</small>
            </div>
          </div>
          {error && (
            <p id="supplier-form-error" className="form-error" role="alert">
              {error}
            </p>
          )}
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save supplier"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SupplierManager({
  mode = "page",
  selectedId,
  onSelect = () => {},
}) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [internalSelectedId, setInternalSelectedId] = useState(selectedId || "");
  const [search, setSearch] = useState("");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [statusSavingId, setStatusSavingId] = useState(null);
  const [restoreSupplierFocus, setRestoreSupplierFocus] = useState(false);
  const selectRef = useRef(null);
  const createButtonRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    listSuppliers({ includeInactive: mode === "page" })
      .then((result) => {
        if (active) setSuppliers(result);
      })
      .catch((loadError) => {
        if (active) {
          setError(getErrorMessage(loadError, "Suppliers could not be loaded."));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [mode]);

  useEffect(() => {
    if (!restoreSupplierFocus || showCreate) return;
    (selectRef.current || createButtonRef.current)?.focus();
    setRestoreSupplierFocus(false);
  }, [restoreSupplierFocus, showCreate, suppliers]);

  const currentSelectedId = selectedId ?? internalSelectedId;
  const filteredSuppliers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return suppliers;
    return suppliers.filter((supplier) =>
      [supplier.companyName, supplier.phone, supplier.address]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle)),
    );
  }, [search, suppliers]);

  const handleCreated = (supplier) => {
    setSuppliers((current) =>
      [...current, supplier].sort((a, b) =>
        a.companyName.localeCompare(b.companyName),
      ),
    );
    setInternalSelectedId(supplier.id);
    onSelect(supplier.id);
    setShowCreate(false);
    setRestoreSupplierFocus(true);
  };

  const handlePickerChange = (event) => {
    const value = event.target.value ? Number(event.target.value) : "";
    setInternalSelectedId(value);
    onSelect(value || null);
  };

  const loadHistory = async (supplierId) => {
    setInternalSelectedId(supplierId);
    setHistoryLoading(true);
    setError("");
    try {
      setHistory(await listPurchases({ supplierId }));
    } catch (loadError) {
      setError(getErrorMessage(loadError, "Supplier history could not be loaded."));
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleSupplier = async (supplier) => {
    setStatusSavingId(supplier.id);
    setError("");
    try {
      const updated = await setSupplierActive(supplier.id, !supplier.active);
      setSuppliers((current) =>
        current.map((entry) =>
          entry.id === supplier.id ? { ...entry, active: updated.active } : entry,
        ),
      );
    } catch (statusError) {
      setError(getErrorMessage(statusError, "Supplier status could not be changed."));
    } finally {
      setStatusSavingId(null);
    }
  };

  const activeHistory = history.filter((purchase) => purchase.status !== "voided");
  const lifetimeTotal = activeHistory.reduce(
    (total, purchase) => total + Number(purchase.totalAmount || 0),
    0,
  );

  if (mode === "picker") {
    return (
      <section className="supplier-picker" aria-labelledby="purchase-supplier-label">
        <div className="supplier-picker-heading">
          <div>
            <label id="purchase-supplier-label" htmlFor="purchase-supplier">
              Supplier company *
            </label>
            <small>ပစ္စည်းပေးသွင်းသည့် ကုမ္ပဏီကို ရွေးပါ။</small>
          </div>
          {suppliers.length > 0 && (
            <button
              ref={createButtonRef}
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowCreate(true)}
            >
              + New supplier
            </button>
          )}
        </div>
        {loading ? (
          <p>Loading suppliers...</p>
        ) : suppliers.length === 0 ? (
          <div className="supplier-empty-state">
            <strong>No suppliers yet</strong>
            <p>ပထမဆုံး ပစ္စည်းပေးသွင်းသူကို ထည့်ပြီး စတင်ပါ။</p>
            <button
              ref={createButtonRef}
              type="button"
              className="btn btn-primary"
              onClick={() => setShowCreate(true)}
            >
              Add first supplier
            </button>
          </div>
        ) : (
          <select
            ref={selectRef}
            id="purchase-supplier"
            value={currentSelectedId}
            onChange={handlePickerChange}
            required
          >
            <option value="">Choose supplier</option>
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.companyName}
              </option>
            ))}
          </select>
        )}
        {error && <p className="form-error" role="alert">{error}</p>}
        {showCreate && (
          <SupplierDialog
            onCancel={() => {
              setShowCreate(false);
              setRestoreSupplierFocus(true);
            }}
            onCreated={handleCreated}
          />
        )}
      </section>
    );
  }

  const selectedSupplier = suppliers.find(
    (supplier) => String(supplier.id) === String(currentSelectedId),
  );

  return (
    <section className="supplier-manager" aria-labelledby="supplier-manager-title">
      <header className="supplier-manager-header">
        <div>
          <p className="eyebrow">PURCHASE OPERATIONS</p>
          <h2 id="supplier-manager-title">Suppliers</h2>
          <p>ပစ္စည်းပေးသွင်းသည့် ကုမ္ပဏီများနှင့် ဝယ်ယူမှုမှတ်တမ်း</p>
        </div>
        <button
          ref={createButtonRef}
          type="button"
          className="btn btn-primary"
          onClick={() => setShowCreate(true)}
        >
          + New supplier
        </button>
      </header>

      <label className="supplier-search">
        <span>Search suppliers</span>
        <input
          className="purchase-search-input"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Company, phone, or address"
        />
      </label>
      {error && <p className="form-error" role="alert">{error}</p>}

      <div className="supplier-manager-layout">
        <div className="supplier-list" aria-busy={loading}>
          {loading ? (
            <p>Loading suppliers...</p>
          ) : filteredSuppliers.length === 0 ? (
            <div className="supplier-empty-state">
              <strong>{suppliers.length ? "No matching suppliers" : "No suppliers yet"}</strong>
              <p>ပစ္စည်းပေးသွင်းသူ အသစ်ထည့်နိုင်ပါသည်။</p>
            </div>
          ) : (
            filteredSuppliers.map((supplier) => (
              <article
                key={supplier.id}
                className={`supplier-card${String(currentSelectedId) === String(supplier.id) ? " is-selected" : ""}`}
              >
                <button
                  type="button"
                  className="supplier-card-main"
                  data-supplier-id={supplier.id}
                  onClick={() => loadHistory(supplier.id)}
                >
                  <span className="supplier-card-title">{supplier.companyName}</span>
                  <span>{supplier.phone || "No phone"}</span>
                  <span>{supplier.address || "No address"}</span>
                </button>
                <div className="supplier-card-status">
                  <span className={`status-badge ${supplier.active ? "is-active" : "is-inactive"}`}>
                    {supplier.active ? "Active" : "Inactive"}
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    data-supplier-status={supplier.id}
                    onClick={() => toggleSupplier(supplier)}
                    disabled={statusSavingId === supplier.id}
                  >
                    {statusSavingId === supplier.id
                      ? "Saving..."
                      : supplier.active
                        ? "Deactivate"
                        : "Reactivate"}
                  </button>
                </div>
              </article>
            ))
          )}
        </div>

        <aside className="supplier-history" aria-live="polite">
          {selectedSupplier ? (
            <>
              <h3>{selectedSupplier.companyName}</h3>
              <p>{selectedSupplier.phone || "No phone added"}</p>
              <p>{selectedSupplier.address || "No address added"}</p>
              {historyLoading ? (
                <p>Loading purchase history...</p>
              ) : (
                <dl className="supplier-history-summary">
                  <div><dt>Completed purchases</dt><dd>{activeHistory.length}</dd></div>
                  <div><dt>Lifetime purchased</dt><dd>MMK {formatCurrency(lifetimeTotal)}</dd></div>
                </dl>
              )}
            </>
          ) : (
            <div className="supplier-history-empty">
              <strong>Select a supplier</strong>
              <p>ဝယ်ယူမှုမှတ်တမ်းနှင့် စုစုပေါင်းကို ဒီနေရာတွင် ကြည့်နိုင်ပါသည်။</p>
            </div>
          )}
        </aside>
      </div>

      {showCreate && (
        <SupplierDialog
          onCancel={() => {
            setShowCreate(false);
            setRestoreSupplierFocus(true);
          }}
          onCreated={handleCreated}
        />
      )}
    </section>
  );
}
